import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import StripeCallbackPage from './pages/StripeCallbackPage'
import { useAuth } from './hooks/useAuth'
import LoginPage from './pages/LoginPage'
import CardsPage from './pages/CardsPage'
import CheckinPage from './pages/CheckinPage'
import SchedulePage from './pages/SchedulePage'
import CompetitionsPage from './pages/CompetitionsPage'
import PublicCheckinPage from './pages/PublicCheckinPage'
import InvitePage from './pages/InvitePage'
import MyCardPage from './pages/MyCardPage'
import SettingsPage from './pages/SettingsPage'
import PinLoginPage from './pages/PinLoginPage'
import ParentPage from './pages/ParentPage'
import PublicProfilePage from './pages/PublicProfilePage'
import BillingPage from './pages/BillingPage'

const NAV = [
  { id: 'cards', label: 'Cards', icon: '🃏' },
  { id: 'checkin', label: 'Check-in', icon: '✅' },
  { id: 'schedule', label: 'Schedule', icon: '📅' },
  { id: 'competitions', label: 'Competitions', icon: '🏆' },
  { id: 'billing', label: 'Billing', icon: '💳' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
]

export default function App() {


  return (
    <BrowserRouter>
      <style>{`
        @keyframes shine { 0%{left:-80%} 60%{left:130%} 100%{left:130%} }
        @keyframes promotionPulse { 0%,100%{opacity:0.3} 50%{opacity:0.8} }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, select, button, textarea { font-family: inherit; }
        input { padding: 8px 10px; border-radius: 8px; border: 1px solid #ddd; font-size: 13px; width: 100%; outline: none; }
        input:focus { border-color: #1860a8; box-shadow: 0 0 0 3px rgba(24,96,168,0.1); }
        select { padding: 8px 10px; border-radius: 8px; border: 1px solid #ddd; font-size: 13px; outline: none; background: #fff; }
        select:focus { border-color: #1860a8; }
      `}</style>
      <Routes>
        {/* Public routes — no login needed */}
        <Route path="/checkin/:classId" element={<PublicCheckinPage />} />
        <Route path="/join/:clubSlug" element={<InvitePage />} />
        <Route path="/pin/:clubSlug" element={<PinLoginPage />} />
        <Route path="/fighter/:passportId" element={<PublicProfilePage />} />
        <Route path="/parent" element={<ParentPage />} />
        {/* Fighter's own card — requires login but not admin */}
        <Route path="/my-card" element={<ProtectedRoute><MyCardPage /></ProtectedRoute>} />
        {/* Admin app */}
        <Route path="/stripe-callback" element={<StripeCallbackPage />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/app" element={<AdminApp />} />
        <Route path="/app/*" element={<AdminApp />} />
        <Route path="/*" element={<AdminApp />} />
      </Routes>
    </BrowserRouter>
  )
}

function LandingPage() {
  const { user, loading } = useAuth()
  if (!loading && user) return <Navigate to="/app" replace />
  return (
    <div style={{ background: '#0a0a0f', color: '#f0f0f8', fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>
      <style>{String.raw`
        @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');
        .lp-btn { background: linear-gradient(135deg,#4f6ef7,#7c3aed); color:#fff; border:none; border-radius:10px; padding:14px 28px; font-size:16px; font-weight:600; cursor:pointer; text-decoration:none; display:inline-block; transition:transform 0.15s; box-shadow:0 4px 20px rgba(79,110,247,0.35); }
        .lp-btn:hover { transform:translateY(-1px); }
        .lp-ghost { background:transparent; color:#6b6b80; border:1px solid rgba(255,255,255,0.1); border-radius:10px; padding:14px 24px; font-size:15px; cursor:pointer; text-decoration:none; display:inline-block; }
        .lp-ghost:hover { color:#f0f0f8; }
        .lp-feat { background:#111118; border:1px solid rgba(255,255,255,0.08); border-radius:16px; padding:28px; transition:background 0.2s; }
        .lp-feat:hover { background:#1a1a24; }
        .lp-card { border-radius:14px; overflow:hidden; position:relative; display:flex; flex-direction:column; flex-shrink:0; transition:transform 0.3s; }
        .lp-card:hover { transform:translateY(-6px) scale(1.02); }
        .lp-shine { position:absolute; top:0; left:-80%; width:40%; height:100%; background:linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.18) 50%,transparent 70%); animation:shine 4s infinite; pointer-events:none; z-index:10; }
        @media (max-width:768px) { .lp-cards { display:none !important; } * { max-width:100vw; } }
      `}</style>
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 5%', height:64, background:'rgba(10,10,15,0.88)', backdropFilter:'blur(12px)', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontFamily:"'VT323',monospace", fontSize:22 }}>MyAthlete<span style={{ color:'#4f6ef7' }}>Card</span></div>
        <a href="/app" className="lp-btn" style={{ padding:'8px 20px', fontSize:14 }}>Sign in</a>
      </nav>
      <section style={{ minHeight:'100vh', display:'flex', alignItems:'center', padding:'100px 5% 60px', maxWidth:1200, margin:'0 auto', gap:60, flexWrap:'wrap' }}>
        <div style={{ flex:'1 1 300px' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(79,110,247,0.12)', border:'1px solid rgba(79,110,247,0.3)', borderRadius:20, padding:'4px 14px', fontSize:12, fontWeight:600, color:'#4f6ef7', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:24 }}>🥋 Built for BJJ gyms</div>
          <h1 style={{ fontSize:'clamp(36px,5vw,60px)', fontWeight:700, lineHeight:1.1, letterSpacing:'-0.02em', marginBottom:20 }}>Your fighters deserve<br /><span style={{ color:'#4f6ef7' }}>a real card</span></h1>
          <p style={{ fontSize:18, color:'#6b6b80', maxWidth:480, marginBottom:36, lineHeight:1.7 }}>MyAthleteCard gives every member a digital fighter card that tracks skills, sessions, belts, and competition results.</p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
            <a href="/app" className="lp-btn">Get started free</a>
            <a href="#features" className="lp-ghost">See features</a>
          </div>
          <div style={{ marginTop:16, fontSize:14, color:'#6b6b80' }}>Already have an account? <a href="/app" style={{ color:'#4f6ef7', textDecoration:'none', fontWeight:600 }}>Sign in</a></div>
        </div>
        <div className="lp-cards" style={{ flex:'0 0 auto', display:'flex', gap:16, alignItems:'center' }}>
          {[
            {bg:'linear-gradient(155deg,#fff,#f0f0f0 40%,#fff 60%,#e8e8e8)',border:'#d0d0d0',rating:'72',c:'#1a1a1a',m:'#444',name:'LUKA KOVAC',d:'0s',y:0},
            {bg:'linear-gradient(155deg,#3888d0,#0c4888 40%,#48a0e0 60%,#082868)',border:'#1860a8',rating:'87',c:'#fff',m:'rgba(255,255,255,0.6)',name:'MARIO SILVA',d:'1.5s',y:22},
            {bg:'linear-gradient(155deg,#303030,#0a0a0a 40%,#404040 60%,#080808)',border:'#c8a030',rating:'96',c:'#e8c840',m:'#c8a030',name:'KENJI TANAKA',d:'2.8s',y:-10}
          ].map((card,i) => (
            <div key={i} className="lp-card" style={{ width:175, height:285, background:card.bg, border:'2.5px solid '+card.border, color:card.c, transform:'translateY('+card.y+'px)' }}>
              <div className="lp-shine" style={{ animationDelay:card.d }}></div>
              <div style={{ position:'absolute', top:0, left:0, width:'38%', height:'100%', background:'linear-gradient(to right,rgba(0,0,0,0.12),transparent)', zIndex:1, pointerEvents:'none' }}></div>
              <div style={{ position:'absolute', top:7, left:7, textAlign:'center', zIndex:3, lineHeight:1 }}>
                <div style={{ fontFamily:"'VT323',monospace", fontSize:36, color:card.c, lineHeight:1 }}>{card.rating}</div>
                <div style={{ fontFamily:"'VT323',monospace", fontSize:10, color:card.m, letterSpacing:'0.1em' }}>BJJ</div>
              </div>
              <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ fontSize:50 }}>🥋</div></div>
              <div style={{ padding:'5px 8px 8px', zIndex:2 }}>
                <div style={{ fontFamily:"'VT323',monospace", fontSize:13, textAlign:'center', color:card.c, marginBottom:3 }}>{card.name}</div>
                <div style={{ height:1, background:card.c, marginBottom:4, opacity:0.2 }}></div>
                <div style={{ display:'flex', justifyContent:'space-around' }}>
                  {[['64','TKD'],['71','GRD'],['58','PAS'],['80','SUB']].map(([v,l]) => (
                    <div key={l} style={{ textAlign:'center' }}>
                      <div style={{ fontFamily:"'VT323',monospace", fontSize:14, color:card.c }}>{v}</div>
                      <div style={{ fontFamily:"'VT323',monospace", fontSize:7, color:card.m, textTransform:'uppercase' }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', borderBottom:'1px solid rgba(255,255,255,0.08)', padding:'20px 5%', display:'flex', alignItems:'center', justifyContent:'center', gap:48, flexWrap:'wrap' }}>
        {[['500+','Fighter cards'],['BJJ','Built for grappling'],['Free','To get started'],['5min','Setup time']].map(([n,l]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontSize:22, fontWeight:700 }}>{n}</div>
            <div style={{ fontSize:13, color:'#6b6b80' }}>{l}</div>
          </div>
        ))}
      </div>
      <div id="features" style={{ padding:'80px 5%', maxWidth:1200, margin:'0 auto' }}>
        <div style={{ fontSize:12, fontWeight:600, color:'#4f6ef7', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>Features</div>
        <h2 style={{ fontSize:'clamp(28px,4vw,44px)', fontWeight:700, letterSpacing:'-0.02em', marginBottom:48 }}>Everything your gym needs.</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16 }}>
          {[['🃏','Fighter Cards — now MyAthleteCard','Every member gets a card that updates automatically.'],['📊','Skill Tracking','Ratings update with every class session.'],['✅','QR Check-in','Members scan to check in. Sessions log automatically.'],['💳','Billing','Stripe payments or manual cash recording.'],['🏆','Competitions','Log medals. Wins appear on the fighter card.'],['🎖️','Badges','Members earn badges for milestones.']].map(([icon,title,desc]) => (
            <div key={title} className="lp-feat">
              <div style={{ fontSize:26, marginBottom:14 }}>{icon}</div>
              <div style={{ fontSize:16, fontWeight:600, marginBottom:8 }}>{title}</div>
              <div style={{ fontSize:14, color:'#6b6b80', lineHeight:1.65 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding:'80px 5%', textAlign:'center', maxWidth:700, margin:'0 auto' }}>
        <h2 style={{ fontSize:'clamp(28px,4vw,44px)', fontWeight:700, letterSpacing:'-0.02em', marginBottom:16 }}>Your gym. Your fighters. Their cards.</h2>
        <p style={{ fontSize:17, color:'#6b6b80', marginBottom:36 }}>Free to start, no credit card required.</p>
        <a href="/app" className="lp-btn" style={{ fontSize:17, padding:'16px 36px' }}>Create your gym for free</a>
      </div>
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', padding:'28px 5%', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
        <div style={{ fontFamily:"'VT323',monospace", fontSize:18, color:'#6b6b80' }}>MyAthlete<span style={{ color:'#4f6ef7' }}>Card</span></div>
        <div style={{ fontSize:13, color:'#6b6b80' }}>2026 MyAthleteCard</div>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/app" replace />
  return children
}

function AdminApp() {
  const { user, profile, club, loading, signOut } = useAuth()
  const params = new URLSearchParams(window.location.search)
  const initialPage = params.get('redirect') || 'cards'
  const [page, setPage] = useState(initialPage)

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6fb' }}>
      <div style={{ color: '#888', fontSize: 14 }}>Loading...</div>
    </div>
  )

  if (!user) return <LoginPage />

  // If logged in as athlete (not admin), redirect to their card
  if (profile?.role === 'athlete') return <Navigate to="/my-card" replace />

  const PageComponent = {
    cards: CardsPage,
    checkin: CheckinPage,
    schedule: SchedulePage,
    competitions: CompetitionsPage,
    billing: BillingPage,
    settings: SettingsPage
  }[page]

  const inviteUrl = club ? `${window.location.origin}/join/${club.slug}` : ''

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <header style={{
        background: '#fff', borderBottom: '1px solid #eee',
        padding: '0 1.5rem', display: 'flex', alignItems: 'center', gap: 10,
        height: 56, position: 'sticky', top: 0, zIndex: 100, flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
          <div style={{ background: 'linear-gradient(135deg,#1860a8,#0c4480)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🥋</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a', lineHeight: 1 }}>{club?.name || 'MyAthleteCard'}</div>
            <div style={{ fontSize: 10, color: '#888', lineHeight: 1 }}>Admin</div>
          </div>
        </div>

        {NAV.map(n => (
          <button key={n.id} onClick={() => setPage(n.id)} style={{
            background: page === n.id ? '#eef4ff' : 'transparent',
            color: page === n.id ? '#1860a8' : '#555',
            border: 'none', borderRadius: 8, padding: '6px 12px',
            fontSize: 13, cursor: 'pointer', fontWeight: page === n.id ? 600 : 400,
            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap'
          }}>
            <span style={{ fontSize: 14 }}>{n.icon}</span>
            <span>{n.label}</span>
          </button>
        ))}

        <button onClick={() => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(inviteUrl).then(() => alert('Invite link copied!\n\n' + inviteUrl))
          } else {
            const el = document.createElement('textarea')
            el.value = inviteUrl
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            alert('Invite link copied!\n\n' + inviteUrl)
          }
        }} style={{
          background: '#eaf3ff', color: '#1860a8', border: '1px solid #c0d8f0',
          borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 500
        }}>
          📋 Invite link
        </button>

        <button onClick={signOut} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '5px 10px', fontSize: 12, color: '#888', cursor: 'pointer' }}>
          Sign out
        </button>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>
            {NAV.find(n => n.id === page)?.label}
          </h2>
        </div>
        <PageComponent />
      </main>
    </div>
  )
}
